import { computed } from '@ember/object';
import { map, not } from '@ember/object/computed';

import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { trackedFunction } from 'reactiveweb/function';
import { TrackedObject } from 'tracked-built-ins';

import { CMS_LANGUAGE_IDS } from 'umg-ember-backend/utils/cms-languages';
import { saveBelongsToRelationship } from 'umg-ember-backend/utils/model-save-hooks';
import {
  buildValidations,
  validator,
} from 'umg-ember-backend/utils/validations';

const fontFamiliesToLoad = map(
  'model.options.fontFamiliesToLoad',
  (fontFamily) => {
    return fontFamily.familyWithFallback;
  },
);

const Validations = buildValidations({
  cmsLanguageId: validator('inclusion', { in: CMS_LANGUAGE_IDS }),
  artist: [validator('belongs-to'), validator('presence', true)],
  headline: validator('presence', true),
  ['options.fontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.isV2'),
  }),
  ['options.headlineFontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.isV2'),
  }),
  ['options.headerFontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.isV2'),
  }),
  pages: [validator('has-many'), validator('presence', true)],
});

function defaultValueOptions() {
  return new TrackedObject({
    template: 'standard',
  });
}

export const settings = {
  saveHooks: {
    before: [saveBelongsToRelationship('newsletter')],
  },
  copyableOptions(record) {
    return {
      ignoreAttributes: [
        'artist',
        'seoTitle',
        'seoDescription',
        'favicon',
        'newsletter',
        'seoImage',
        'preview',
        'template',
        'thumbnail',
        'vhost',
      ],
      overwrite: {
        options: {
          ...record.options,
          customHeader: undefined,
        },
      },
      relationsToCopy: ['pages'],
    };
  },
};

export default class MlpModel extends Model.extend(Validations) {
  // Attributes
  @attr('boolean') hasPublishedMlp;
  @attr('string') headline;
  @attr('boolean') isPreview;
  @attr('boolean') isMultiArtistPage;
  @attr('string') liveUrl;
  @attr('boolean') multiPage;
  @attr('json', { defaultValue: defaultValueOptions }) options;
  @attr('string') previewUrl;
  @attr() shops;
  @attr('boolean') sso;
  @attr('boolean') shopifyHasCartPage;
  @attr('boolean') template;
  @attr('string', { defaultValue: 'default-v1' }) theme;
  @attr('string') urlPath;
  @attr('string', { defaultValue: '2' }) version;
  @attr('array') zatapModelIds;

  // CMS fields
  @attr('string', { defaultValue: '1' }) cmsLanguageId;
  @attr cmsKilldate;
  @attr cmsLivedate;
  @attr('string') seoTitle;
  @attr('string') seoDescription;
  @attr('number', { defaultValue: 1 }) cmsState;

  // Meta fields
  @attr cmsCreated;
  @attr cmsDeleted;
  @attr cmsLocktime;
  @attr cmsModified;
  @attr() cmsHistory;
  @belongsTo('user', { async: true, inverse: null }) cmsOwnerId;
  @belongsTo('user', { async: true, inverse: null }) cmsCreatorId;
  @belongsTo('user', { async: true, inverse: null }) cmsLockerId;
  @belongsTo('user', { async: true, inverse: null }) cmsModifierId;

  // Relations
  @belongsTo('artist', { async: true, inverse: null }) artist;
  @belongsTo('asset', { async: true, inverse: null }) backgroundImage;
  @belongsTo('asset', { async: true, inverse: null }) backgroundVideo;
  @belongsTo('channel', { async: true, inverse: null }) channel;
  @belongsTo('asset', { async: true, inverse: null }) favicon;
  @hasMany('link', { async: true, inverse: null }) footerLinks;
  @belongsTo('asset', { async: true, inverse: null }) headerArtistCustomLogo;
  @belongsTo('asset', { async: true, inverse: null }) seoImage;
  @belongsTo('channel', { inverse: null, async: true }) ownedByChannel;
  @hasMany('mlp-page', { async: false, inverse: 'mlp' }) pages;
  @belongsTo('mlp', { async: true, inverse: null }) publishedMlp;
  @hasMany('mlp', { async: true, inverse: null }) relatedMlps;
  @belongsTo('asset', { async: true, inverse: null }) thumbnail;
  @belongsTo('vhost', { async: true, inverse: null }) vhost;
  @belongsTo('newsletter', { async: true, inverse: null }) newsletter;
  @belongsTo('shopify-store', { async: true, inverse: null }) shopifyStore;

  // Getter and setter
  get displayHeadline() {
    return this.headline;
  }

  get displayLabel() {
    return this.headline;
  }

  @computed('artist')
  get hasArtist() {
    return this.belongsTo('artist').id() !== null;
  }

  @computed('channel')
  get hasChannel() {
    return this.belongsTo('channel').id() !== null;
  }

  get hasCustomTemplate() {
    return this.options.template !== 'standard';
  }

  get hasInvalidMainSettings() {
    const hasChannelOrArtist = this.hasArtist || this.hasChannel;

    return !this.headline || !hasChannelOrArtist;
  }

  get hasVisiblePublisheMlp() {
    const publishedMlp = this.resolvedPublishedMlp.value ?? null;

    return !!this.hasPublishedMlp && publishedMlp?.cmsState === 2;
  }

  get hasUnpublishedChanges() {
    const publishedMlp = this.resolvedPublishedMlp.value ?? null;
    if (!this.hasVisiblePublisheMlp) {
      return true;
    }

    return publishedMlp?.cmsModified < this.cmsModified;
  }

  get isV2() {
    return this.version === '2';
  }

  get visibleLiveUrl() {
    if (this.hasVisiblePublisheMlp) {
      return this.liveUrl;
    }

    return null;
  }

  // Functions
  resolvedPublishedMlp = trackedFunction(this, async () => {
    return await this.publishedMlp;
  });

  // TODO: Do we find a better solution?
  resetOptions() {
    this.options = defaultValueOptions();
    this.headerArtistCustomLogo = null;
    this.backgroundImage = null;
    this.backgroundVideo = null;

    this.footerLinks.clear();
  }

  preview() {
    window.open(this.previewUrl, `mlp_preview_${this.id}`).focus();
  }
}
